import { Dismiss } from 'flowbite';

export type ToastVariant = 'success' | 'error' | 'warning';

export interface ShowToastOptions {
    variant: ToastVariant;
    message: string;
    title?: string;
    durationMs?: number;
}

let toastCounter = 0;

const variantIconClasses: Record<ToastVariant, string> = {
    success:
        'inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded dark:bg-green-800 dark:text-green-200',
    error: 'inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-red-500 bg-red-100 rounded dark:bg-red-800 dark:text-red-200',
    warning:
        'inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-amber-600 bg-amber-100 rounded dark:bg-amber-900/40 dark:text-amber-200',
};

const variantSymbols: Record<ToastVariant, string> = {
    success: '✓',
    error: '!',
    warning: '!',
};

/**
 * Flowbite-aligned toast stack (see https://flowbite.com/docs/components/toast/).
 * Uses Dismiss for close control and optional auto-hide.
 */
export function showToast(options: ShowToastOptions): void {
    const root = document.getElementById('issue-tracker-toast-root');
    if (!root) {
        return;
    }

    const id = `issue-tracker-toast-${++toastCounter}`;
    const duration =
        options.durationMs ?? (options.variant === 'error' || options.variant === 'warning' ? 8000 : 5000);

    const toast = document.createElement('div');
    toast.id = id;
    toast.setAttribute('role', 'alert');
    toast.className =
        'pointer-events-auto flex items-center w-full max-w-sm p-4 text-gray-500 bg-white border border-gray-200 rounded-lg shadow dark:text-gray-400 dark:bg-gray-800 dark:border-gray-700';

    const iconWrap = document.createElement('div');
    iconWrap.className = variantIconClasses[options.variant];
    const iconSym = document.createElement('span');
    iconSym.className = 'text-sm font-semibold leading-none';
    iconSym.textContent = variantSymbols[options.variant];
    iconWrap.appendChild(iconSym);

    const body = document.createElement('div');
    body.className = 'ms-3 text-sm font-normal flex-1 min-w-0';
    if (options.title) {
        const titleEl = document.createElement('div');
        titleEl.className = 'font-semibold text-gray-900 dark:text-white mb-1';
        titleEl.textContent = options.title;
        body.appendChild(titleEl);
    }
    const msgEl = document.createElement('div');
    msgEl.textContent = options.message;
    body.appendChild(msgEl);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className =
        'ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700';
    closeBtn.setAttribute('data-dismiss-target', `#${id}`);
    closeBtn.setAttribute('aria-label', 'Close');
    const srOnly = document.createElement('span');
    srOnly.className = 'sr-only';
    srOnly.textContent = 'Close';
    closeBtn.appendChild(srOnly);
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('class', 'w-3 h-3');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('viewBox', '0 0 14 14');
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('d', 'm1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6');
    svg.appendChild(path);
    closeBtn.appendChild(svg);

    toast.appendChild(iconWrap);
    toast.appendChild(body);
    toast.appendChild(closeBtn);

    root.appendChild(toast);

    const dismiss = new Dismiss(
        toast,
        closeBtn,
        {
            onHide: () => {
                window.setTimeout(() => {
                    toast.remove();
                }, 350);
            },
        },
        { id, override: true },
    );

    window.setTimeout(() => {
        if (toast.isConnected) {
            dismiss.hide();
        }
    }, duration);
}

interface PageToastPayload {
    variant?: string;
    message?: string;
    title?: string;
}

/**
 * Reads `<script type="application/json" id="issue-tracker-page-toasts">` if present.
 */
export function initToastsFromPage(): void {
    const el = document.getElementById('issue-tracker-page-toasts');
    if (!el || el.textContent === null || el.textContent.trim() === '') {
        return;
    }
    try {
        const items = JSON.parse(el.textContent) as PageToastPayload[];
        if (!Array.isArray(items)) {
            return;
        }
        for (const item of items) {
            if (!item?.message) {
                continue;
            }
            const v = item.variant;
            if (v === 'success' || v === 'error' || v === 'warning') {
                showToast({
                    variant: v,
                    message: item.message,
                    title: item.title,
                });
            }
        }
    } catch {
        /* malformed JSON — ignore */
    }
}
